/**
 * SupportTicketSystem.js
 * ─────────────────────────────────────────────────────────────
 * Complete in-dashboard support ticket system for the user dashboard.
 *
 * Views:
 *   1. Ticket List — all user's tickets with status badges
 *   2. Ticket Detail — threaded conversation with real-time updates
 *   3. Create Ticket — form with subject dropdown + description
 *
 * Integrates with Firestore `supportTickets` collection and
 * `supportTickets/{id}/messages` subcollection.
 * ─────────────────────────────────────────────────────────────
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    collection, addDoc, doc, getDoc, getDocs, updateDoc, onSnapshot,
    query, where, orderBy, Timestamp, runTransaction, serverTimestamp
} from 'firebase/firestore';
import { db, auth } from '../firebase-config';
import THEME from '../theme';

// ─────────────────────────────────────────────────────────────
// CONSTANTS
// ─────────────────────────────────────────────────────────────

const TICKET_CATEGORIES = [
    { value: 'payment_issue', label: 'Payment Issue', icon: '💳' },
    { value: 'subscription', label: 'Subscription / Activation', icon: '⭐' },
    { value: 'account_login', label: 'Account / Login Problem', icon: '🔐' },
    { value: 'bill_discrepancy', label: 'Mess Bill Discrepancy', icon: '📊' },
    { value: 'rebate_issue', label: 'Rebate / Leave Issue', icon: '📋' },
    { value: 'privacy_data', label: 'Privacy or Data Request', icon: '🛡️' },
    { value: 'bug_report', label: 'Bug Report', icon: '🐛' },
    { value: 'feature_request', label: 'Feature Request', icon: '💡' },
    { value: 'other', label: 'Other', icon: '📩' },
];

const STATUS_CONFIG = {
    open: { label: 'Open', color: '#22C55E', bg: 'rgba(34,197,94,0.1)', border: 'rgba(34,197,94,0.3)', icon: '🟢' },
    in_progress: { label: 'In Progress', color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.3)', icon: '🔵' },
    resolved: { label: 'Resolved', color: '#14B8A6', bg: 'rgba(20,184,166,0.1)', border: 'rgba(20,184,166,0.3)', icon: '✅' },
    closed: { label: 'Closed', color: '#64748B', bg: 'rgba(100,116,139,0.1)', border: 'rgba(100,116,139,0.3)', icon: '⚪' },
};

const PRIORITY_CONFIG = {
    low: { label: 'Low', color: '#64748B' },
    medium: { label: 'Medium', color: '#F59E0B' },
    high: { label: 'High', color: '#EF4444' },
};

// ─────────────────────────────────────────────────────────────
// HELPER: Generate ticket number via Firestore counter
// ─────────────────────────────────────────────────────────────
async function generateTicketNumber() {
    const counterRef = doc(db, 'counters', 'supportTickets');
    let ticketNum = 1;
    try {
        await runTransaction(db, async (transaction) => {
            const counterDoc = await transaction.get(counterRef);
            if (counterDoc.exists()) {
                ticketNum = (counterDoc.data().count || 0) + 1;
                transaction.update(counterRef, { count: ticketNum });
            } else {
                ticketNum = 1;
                transaction.set(counterRef, { count: 1 });
            }
        });
    } catch (err) {
        // Fallback: use timestamp-based number
        ticketNum = Date.now() % 100000;
    }
    return `TKT-${String(ticketNum).padStart(5, '0')}`;
}

// ─────────────────────────────────────────────────────────────
// HELPER: Format relative time
// ─────────────────────────────────────────────────────────────
function timeAgo(timestamp) {
    if (!timestamp) return '';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHrs < 24) return `${diffHrs}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function formatFullDate(timestamp) {
    if (!timestamp) return '';
    const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
    return date.toLocaleDateString('en-IN', {
        day: 'numeric', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
    });
}

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENT: Status Badge
// ─────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.open;
    return (
        <span style={{
            backgroundColor: cfg.bg,
            color: cfg.color,
            border: `1px solid ${cfg.border}`,
            padding: '2px 10px',
            borderRadius: '9999px',
            fontSize: '11px',
            fontWeight: 600,
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            whiteSpace: 'nowrap',
        }}>
            {cfg.icon} {cfg.label}
        </span>
    );
};

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENT: Empty State
// ─────────────────────────────────────────────────────────────
const EmptyState = ({ onCreateTicket }) => (
    <div style={{ textAlign: 'center', padding: '60px 20px' }}>
        <div style={{
            width: 80, height: 80, margin: '0 auto 20px', borderRadius: 20,
            background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.2)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36,
        }}>
            🎫
        </div>
        <h3 style={{ fontFamily: THEME.fonts.heading, fontSize: 18, fontWeight: 700, color: '#F8FAFC', marginBottom: 8 }}>
            No Support Tickets
        </h3>
        <p style={{ color: '#94A3B8', fontSize: 14, marginBottom: 24, maxWidth: 300, margin: '0 auto 24px' }}>
            Need help? Create a support ticket and we'll get back to you as soon as possible.
        </p>
        <button
            onClick={onCreateTicket}
            style={{
                backgroundColor: THEME.colors.goldAccent, color: '#0F172A',
                padding: '12px 28px', borderRadius: 12, fontWeight: 700, fontSize: 14,
                border: 'none', cursor: 'pointer', transition: 'all 0.2s',
            }}
        >
            + Create Ticket
        </button>
    </div>
);

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENT: Ticket Card (for list view)
// ─────────────────────────────────────────────────────────────
const TicketCard = ({ ticket, onClick }) => {
    const category = TICKET_CATEGORIES.find(c => c.value === ticket.subject) || { icon: '📩', label: ticket.subject };
    return (
        <button
            onClick={onClick}
            style={{
                width: '100%', textAlign: 'left', padding: '16px', borderRadius: 16,
                backgroundColor: THEME.colors.midnightSurface,
                border: '1px solid #334155', cursor: 'pointer',
                transition: 'all 0.2s', display: 'flex', flexDirection: 'column', gap: 10,
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(245,158,11,0.3)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = '#334155'; e.currentTarget.style.transform = 'translateY(0)'; }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>{category.icon}</span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <p style={{ fontFamily: THEME.fonts.heading, fontSize: 14, fontWeight: 600, color: '#F8FAFC', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {ticket.ticketNumber} — {category.label}
                        </p>
                        <p style={{ fontSize: 12, color: '#94A3B8', margin: '2px 0 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {ticket.description}
                        </p>
                    </div>
                </div>
                <StatusBadge status={ticket.status} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 11, color: '#64748B' }}>
                <span>Created {timeAgo(ticket.createdAt)}</span>
                {ticket.lastReplyBy && (
                    <span style={{ color: ticket.lastReplyBy === 'admin' ? '#F59E0B' : '#64748B' }}>
                        {ticket.lastReplyBy === 'admin' ? '💬 Admin replied' : 'You replied'} {timeAgo(ticket.lastReplyAt)}
                    </span>
                )}
            </div>
        </button>
    );
};

// ─────────────────────────────────────────────────────────────
// SUB-COMPONENT: Message Bubble
// ─────────────────────────────────────────────────────────────
const MessageBubble = ({ message }) => {
    const isUser = message.senderType === 'user';
    return (
        <div style={{
            display: 'flex', flexDirection: 'column',
            alignItems: isUser ? 'flex-end' : 'flex-start',
            marginBottom: 12,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                {!isUser && (
                    <div style={{
                        width: 24, height: 24, borderRadius: 8,
                        background: 'linear-gradient(135deg, #F59E0B, #D97706)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, color: '#fff', fontWeight: 700,
                    }}>R</div>
                )}
                <span style={{ fontSize: 11, color: '#64748B', fontWeight: 500 }}>
                    {isUser ? 'You' : 'Resido Support'}
                </span>
                <span style={{ fontSize: 10, color: '#475569' }}>
                    {formatFullDate(message.createdAt)}
                </span>
            </div>
            <div style={{
                maxWidth: '85%', padding: '12px 16px', borderRadius: 16,
                backgroundColor: isUser ? 'rgba(245,158,11,0.15)' : '#1E293B',
                border: `1px solid ${isUser ? 'rgba(245,158,11,0.25)' : '#334155'}`,
                borderTopRightRadius: isUser ? 4 : 16,
                borderTopLeftRadius: isUser ? 16 : 4,
            }}>
                <p style={{ fontSize: 14, color: '#F8FAFC', margin: 0, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {message.text}
                </p>
            </div>
        </div>
    );
};


// ═════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════

export default function SupportTicketSystem({ user, userData }) {
    // View state: 'list' | 'detail' | 'create'
    const [view, setView] = useState('list');
    const [selectedTicketId, setSelectedTicketId] = useState(null);

    // Data state
    const [tickets, setTickets] = useState([]);
    const [messages, setMessages] = useState([]);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [ticketsLoading, setTicketsLoading] = useState(true);
    const [messagesLoading, setMessagesLoading] = useState(false);

    // Create ticket form state
    const [formSubject, setFormSubject] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [formPriority, setFormPriority] = useState('medium');
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState(false);

    // Reply state
    const [replyText, setReplyText] = useState('');
    const [replySending, setReplySending] = useState(false);

    const messagesEndRef = useRef(null);

    // ─── Fetch user's tickets (real-time) ───
    useEffect(() => {
        if (!user?.uid) return;

        const q = query(
            collection(db, 'supportTickets'),
            where('userId', '==', user.uid),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const ticketsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setTickets(ticketsData);
            setTicketsLoading(false);
        }, (err) => {
            console.error('Error fetching tickets:', err);
            setTicketsLoading(false);
        });

        return () => unsubscribe();
    }, [user?.uid]);

    // ─── Fetch messages for selected ticket (real-time) ───
    useEffect(() => {
        if (!selectedTicketId) return;

        setMessagesLoading(true);

        // Also fetch the ticket doc
        const ticketRef = doc(db, 'supportTickets', selectedTicketId);
        const unsubTicket = onSnapshot(ticketRef, (snap) => {
            if (snap.exists()) setSelectedTicket({ id: snap.id, ...snap.data() });
        });

        const messagesQuery = query(
            collection(db, 'supportTickets', selectedTicketId, 'messages'),
            orderBy('createdAt', 'asc')
        );

        const unsubMessages = onSnapshot(messagesQuery, (snapshot) => {
            const msgs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            setMessages(msgs);
            setMessagesLoading(false);
            // Auto-scroll to bottom
            setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
        }, (err) => {
            console.error('Error fetching messages:', err);
            setMessagesLoading(false);
        });

        return () => {
            unsubTicket();
            unsubMessages();
        };
    }, [selectedTicketId]);

    // ─── Create Ticket Handler ───
    const handleCreateTicket = useCallback(async () => {
        if (!formSubject || formDescription.trim().length < 20) {
            setFormError('Please select a category and write at least 20 characters.');
            return;
        }

        setFormError('');
        setFormSubmitting(true);

        try {
            const ticketNumber = await generateTicketNumber();
            const now = Timestamp.now();

            const ticketData = {
                ticketNumber,
                subject: formSubject,
                description: formDescription.trim(),
                status: 'open',
                priority: formPriority,
                userId: user.uid,
                userName: userData?.name || user.displayName || '',
                userEmail: userData?.email || user.email || '',
                instituteId: userData?.instituteId || '',
                hostel: userData?.hostel || '',
                source: 'web_dashboard',
                aiChatLog: null,
                assignedTo: null,
                createdAt: now,
                updatedAt: now,
                lastReplyBy: null,
                lastReplyAt: null,
            };

            const ticketRef = await addDoc(collection(db, 'supportTickets'), ticketData);

            // Add the initial description as the first message
            await addDoc(collection(db, 'supportTickets', ticketRef.id, 'messages'), {
                text: formDescription.trim(),
                senderType: 'user',
                senderName: userData?.name || user.displayName || 'User',
                senderId: user.uid,
                createdAt: now,
                attachmentUrl: null,
            });

            setFormSuccess(true);
            setFormSubject('');
            setFormDescription('');
            setFormPriority('medium');

            // Navigate to the new ticket after 1.5s
            setTimeout(() => {
                setFormSuccess(false);
                setSelectedTicketId(ticketRef.id);
                setView('detail');
            }, 1500);

        } catch (err) {
            console.error('Error creating ticket:', err);
            setFormError('Failed to create ticket. Please try again.');
        } finally {
            setFormSubmitting(false);
        }
    }, [formSubject, formDescription, formPriority, user, userData]);

    // ─── Send Reply Handler ───
    const handleSendReply = useCallback(async () => {
        if (!replyText.trim() || !selectedTicketId || replySending) return;

        setReplySending(true);
        try {
            const now = Timestamp.now();

            await addDoc(collection(db, 'supportTickets', selectedTicketId, 'messages'), {
                text: replyText.trim(),
                senderType: 'user',
                senderName: userData?.name || user?.displayName || 'User',
                senderId: user.uid,
                createdAt: now,
                attachmentUrl: null,
            });

            // Update ticket metadata
            await updateDoc(doc(db, 'supportTickets', selectedTicketId), {
                lastReplyBy: 'user',
                lastReplyAt: now,
                updatedAt: now,
            });

            setReplyText('');
        } catch (err) {
            console.error('Error sending reply:', err);
        } finally {
            setReplySending(false);
        }
    }, [replyText, selectedTicketId, replySending, user, userData]);

    // ─── Navigate to ticket detail ───
    const openTicket = (ticketId) => {
        setSelectedTicketId(ticketId);
        setView('detail');
        setReplyText('');
    };

    // ─── Back to list ───
    const goBackToList = () => {
        setView('list');
        setSelectedTicketId(null);
        setSelectedTicket(null);
        setMessages([]);
    };

    // ─── Common styles ───
    const headerStyle = {
        fontFamily: THEME.fonts.heading,
        fontSize: 22,
        fontWeight: 700,
        color: '#F8FAFC',
        margin: 0,
    };

    const backButtonStyle = {
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 6,
        fontSize: 14, fontWeight: 500, padding: '4px 0', marginBottom: 16,
        transition: 'color 0.2s',
    };

    // ═════════════════════════════════════════════════════════════
    // RENDER: CREATE TICKET VIEW
    // ═════════════════════════════════════════════════════════════
    if (view === 'create') {
        return (
            <div style={{ maxWidth: 560, margin: '0 auto' }}>
                <button onClick={goBackToList} style={backButtonStyle}
                    onMouseEnter={e => e.currentTarget.style.color = '#F59E0B'}
                    onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}>
                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                    Back to Tickets
                </button>

                <h2 style={headerStyle}>Create Support Ticket</h2>
                <p style={{ color: '#94A3B8', fontSize: 14, margin: '4px 0 24px' }}>
                    Describe your issue and we'll get back to you within 24 hours.
                </p>

                {formSuccess ? (
                    <div style={{
                        textAlign: 'center', padding: '40px 20px', borderRadius: 16,
                        backgroundColor: 'rgba(20,184,166,0.1)', border: '1px solid rgba(20,184,166,0.25)',
                    }}>
                        <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
                        <p style={{ fontFamily: THEME.fonts.heading, fontSize: 18, fontWeight: 700, color: '#14B8A6', marginBottom: 4 }}>
                            Ticket Created!
                        </p>
                        <p style={{ color: '#94A3B8', fontSize: 14 }}>Redirecting to your ticket...</p>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                        {/* Category Selection */}
                        <div>
                            <label style={{ display: 'block', color: '#94A3B8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                Category *
                            </label>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                                {TICKET_CATEGORIES.map(cat => (
                                    <button
                                        key={cat.value}
                                        onClick={() => setFormSubject(cat.value)}
                                        style={{
                                            padding: '10px 12px', borderRadius: 12, border: '1px solid',
                                            borderColor: formSubject === cat.value ? THEME.colors.goldAccent : '#334155',
                                            backgroundColor: formSubject === cat.value ? 'rgba(245,158,11,0.1)' : THEME.colors.midnightSurface,
                                            color: formSubject === cat.value ? THEME.colors.goldAccent : '#F8FAFC',
                                            cursor: 'pointer', textAlign: 'left', fontSize: 13, fontWeight: 500,
                                            display: 'flex', alignItems: 'center', gap: 8, transition: 'all 0.2s',
                                        }}
                                    >
                                        <span style={{ fontSize: 16 }}>{cat.icon}</span>
                                        {cat.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Priority */}
                        <div>
                            <label style={{ display: 'block', color: '#94A3B8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                Priority
                            </label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                                    <button
                                        key={key}
                                        onClick={() => setFormPriority(key)}
                                        style={{
                                            padding: '8px 16px', borderRadius: 10, border: '1px solid',
                                            borderColor: formPriority === key ? cfg.color : '#334155',
                                            backgroundColor: formPriority === key ? `${cfg.color}15` : 'transparent',
                                            color: formPriority === key ? cfg.color : '#94A3B8',
                                            cursor: 'pointer', fontSize: 13, fontWeight: 600, transition: 'all 0.2s',
                                        }}
                                    >
                                        {cfg.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Description */}
                        <div>
                            <label style={{ display: 'block', color: '#94A3B8', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                                Description *
                            </label>
                            <textarea
                                value={formDescription}
                                onChange={e => setFormDescription(e.target.value)}
                                placeholder="Describe your issue in detail... (min. 20 characters)"
                                rows={5}
                                style={{
                                    width: '100%', padding: '14px 16px', borderRadius: 12,
                                    backgroundColor: '#0F172A', border: '1px solid #334155',
                                    color: '#F8FAFC', fontSize: 14, fontFamily: THEME.fonts.body,
                                    resize: 'vertical', outline: 'none', transition: 'border-color 0.2s',
                                    lineHeight: 1.6, boxSizing: 'border-box',
                                }}
                                onFocus={e => e.currentTarget.style.borderColor = 'rgba(245,158,11,0.5)'}
                                onBlur={e => e.currentTarget.style.borderColor = '#334155'}
                            />
                            <p style={{ fontSize: 11, color: '#64748B', marginTop: 4 }}>
                                {formDescription.length}/1000 characters
                            </p>
                        </div>

                        {formError && (
                            <p style={{ color: '#EF4444', fontSize: 13, margin: 0 }}>{formError}</p>
                        )}

                        <button
                            onClick={handleCreateTicket}
                            disabled={formSubmitting}
                            style={{
                                width: '100%', padding: '14px', borderRadius: 12,
                                backgroundColor: formSubmitting ? '#64748B' : THEME.colors.goldAccent,
                                color: '#0F172A', fontWeight: 700, fontSize: 15,
                                border: 'none', cursor: formSubmitting ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s', display: 'flex', alignItems: 'center',
                                justifyContent: 'center', gap: 8,
                            }}
                        >
                            {formSubmitting ? (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 1s linear infinite' }}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                    Creating Ticket...
                                </>
                            ) : 'Submit Ticket'}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    // ═════════════════════════════════════════════════════════════
    // RENDER: TICKET DETAIL VIEW
    // ═════════════════════════════════════════════════════════════
    if (view === 'detail' && selectedTicket) {
        const category = TICKET_CATEGORIES.find(c => c.value === selectedTicket.subject) || { icon: '📩', label: selectedTicket.subject };
        const isClosedOrResolved = selectedTicket.status === 'closed' || selectedTicket.status === 'resolved';

        return (
            <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 200px)', maxWidth: 640, margin: '0 auto' }}>
                {/* Header */}
                <div style={{ flexShrink: 0 }}>
                    <button onClick={goBackToList} style={backButtonStyle}
                        onMouseEnter={e => e.currentTarget.style.color = '#F59E0B'}
                        onMouseLeave={e => e.currentTarget.style.color = '#94A3B8'}>
                        <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                        Back to Tickets
                    </button>

                    {/* Ticket Info Card */}
                    <div style={{
                        padding: 16, borderRadius: 16,
                        backgroundColor: THEME.colors.midnightSurface,
                        border: '1px solid #334155', marginBottom: 16,
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 20 }}>{category.icon}</span>
                                <span style={{ fontFamily: THEME.fonts.heading, fontSize: 16, fontWeight: 700, color: '#F8FAFC' }}>
                                    {selectedTicket.ticketNumber}
                                </span>
                            </div>
                            <StatusBadge status={selectedTicket.status} />
                        </div>
                        <p style={{ fontSize: 13, color: '#94A3B8', margin: 0 }}>
                            {category.label} • Created {formatFullDate(selectedTicket.createdAt)}
                        </p>
                    </div>
                </div>

                {/* Messages Area */}
                <div style={{
                    flex: 1, overflowY: 'auto', padding: '8px 0',
                    WebkitOverflowScrolling: 'touch',
                }}>
                    {messagesLoading ? (
                        <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>
                            <div style={{ width: 32, height: 32, border: '2px solid #334155', borderTopColor: THEME.colors.goldAccent, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
                            Loading messages...
                        </div>
                    ) : messages.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#64748B', fontSize: 14, padding: 40 }}>No messages yet.</p>
                    ) : (
                        <>
                            {messages.map(msg => (
                                <MessageBubble key={msg.id} message={msg} />
                            ))}
                            <div ref={messagesEndRef} />
                        </>
                    )}
                </div>

                {/* Reply Input */}
                {!isClosedOrResolved ? (
                    <div style={{
                        flexShrink: 0, padding: '12px 0', borderTop: '1px solid #334155',
                    }}>
                        <div style={{
                            display: 'flex', gap: 8, alignItems: 'flex-end',
                            backgroundColor: '#0F172A', borderRadius: 16, padding: '8px 8px 8px 16px',
                            border: '1px solid #334155',
                        }}>
                            <textarea
                                value={replyText}
                                onChange={e => setReplyText(e.target.value)}
                                placeholder="Type your reply..."
                                rows={1}
                                style={{
                                    flex: 1, background: 'none', border: 'none', color: '#F8FAFC',
                                    fontSize: 14, fontFamily: THEME.fonts.body, outline: 'none',
                                    resize: 'none', lineHeight: 1.5, padding: '6px 0',
                                    maxHeight: 100, overflowY: 'auto',
                                }}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        handleSendReply();
                                    }
                                }}
                                onInput={e => {
                                    e.target.style.height = 'auto';
                                    e.target.style.height = Math.min(e.target.scrollHeight, 100) + 'px';
                                }}
                            />
                            <button
                                onClick={handleSendReply}
                                disabled={!replyText.trim() || replySending}
                                style={{
                                    width: 40, height: 40, borderRadius: 12, border: 'none',
                                    backgroundColor: replyText.trim() ? THEME.colors.goldAccent : '#334155',
                                    color: replyText.trim() ? '#0F172A' : '#64748B',
                                    cursor: replyText.trim() ? 'pointer' : 'default',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    flexShrink: 0, transition: 'all 0.2s',
                                }}
                            >
                                {replySending ? (
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
                                        <circle cx="12" cy="12" r="10" strokeOpacity="0.25" /><path d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" fill="currentColor" />
                                    </svg>
                                ) : (
                                    <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M12 5l7 7-7 7" /></svg>
                                )}
                            </button>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        flexShrink: 0, padding: '16px', borderTop: '1px solid #334155',
                        textAlign: 'center', color: '#64748B', fontSize: 13,
                    }}>
                        This ticket has been {selectedTicket.status}. You cannot reply.
                    </div>
                )}

                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    // ═════════════════════════════════════════════════════════════
    // RENDER: TICKET LIST VIEW (default)
    // ═════════════════════════════════════════════════════════════
    return (
        <div style={{ maxWidth: 640, margin: '0 auto' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h2 style={headerStyle}>Support</h2>
                    <p style={{ color: '#94A3B8', fontSize: 13, margin: '2px 0 0' }}>
                        {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
                    </p>
                </div>
                <button
                    onClick={() => { setView('create'); setFormSuccess(false); setFormError(''); }}
                    style={{
                        backgroundColor: THEME.colors.goldAccent, color: '#0F172A',
                        padding: '10px 20px', borderRadius: 12, fontWeight: 700, fontSize: 13,
                        border: 'none', cursor: 'pointer', transition: 'all 0.2s',
                        display: 'flex', alignItems: 'center', gap: 6,
                    }}
                >
                    <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" /></svg>
                    New Ticket
                </button>
            </div>

            {ticketsLoading ? (
                <div style={{ textAlign: 'center', padding: 60, color: '#94A3B8' }}>
                    <div style={{ width: 32, height: 32, border: '2px solid #334155', borderTopColor: THEME.colors.goldAccent, borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
                    Loading tickets...
                    <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
                </div>
            ) : tickets.length === 0 ? (
                <EmptyState onCreateTicket={() => setView('create')} />
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {tickets.map(ticket => (
                        <TicketCard key={ticket.id} ticket={ticket} onClick={() => openTicket(ticket.id)} />
                    ))}
                </div>
            )}

            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}
